// ─────────────────────────────────────────────
//  taskPushPayload — what a phone shows for a DIRECTIVE, and the one automatic
//  directive that is born on the server (emergency_ack).
//
//  A directive lands in the `tasks` collection and is instantly on every open
//  board. This module decides what, if anything, should also wake a phone —
//  because the person a directive is FOR is by definition not looking at the
//  board; they are running a room.
//
//  Pure by design (no Firestore, no admin SDK): the rules that matter most here
//  are "never push the sender their own directive" and "the second acknowledger
//  is silence", and both must be testable without a device or an emulator.
//  functions/taskPush.js does the I/O.
//
//  ⚠️ A DIRECTIVE IS NOT AN EMERGENCY. Everything below is normal priority, on
//  its own quiet channel, with no critical/time-sensitive escalation. The
//  emergency path (src/domain/pushPayload.js) buys its way past Do Not Disturb
//  and Focus modes; that loudness only works while it stays rare. Spend it on a
//  room reassignment and staff mute the app — and then the alarm that means
//  someone is dying arrives on a silenced phone.
//
//  ⚠️ CONFIRM MEANS "I AM AWARE", NOT "I DID IT". No string this module
//  produces may read as completed work. A push that says "task completed" is a
//  claim about the room that nobody made.
// ─────────────────────────────────────────────

import { TASK_KINDS, AUTO_REASONS, MAX_TASK_TEXT } from "./tasks.js";

// The notification channel for directives. Deliberately NOT atc_emergency /
// atc_urgency: a channel is what the user silences, and mixing routine board
// traffic into the emergency channel takes that choice away from them.
export const TASK_CHANNEL_ID = "atc_tasks";

// ── WHO GETS WOKEN ───────────────────────────
//
// `recipientIds` are AUTH uids (resolved from staff ids before the write — a
// directive addressed to a staff id reaches nobody's phone and fails silently).
// The sender is always removed: a board operator does not need their own phone
// to buzz for the instruction they just typed, and an automatic directive's
// "sender" is the system anyway.
export function taskPushRecipients(task) {
  const ids = Array.isArray(task?.recipientIds) ? task.recipientIds : [];
  return [...new Set(ids.filter((id) => id && id !== task?.senderId))];
}

// ── WORDING ──────────────────────────────────
//
// Title says WHERE it came from, body says WHAT it says. The body is the
// directive's own text so the phone and the board never show two different
// wordings of the same instruction.
//
// Every fallback string is about awareness ("sent you", "has seen"), never
// about work being finished. See the wording test — it asserts the absence of
// completion language, which is the kind of regression a reviewer's eye slides
// straight over.
export function buildTaskNotification(task) {
  if (!task) return null;
  const auto = task.kind === TASK_KINDS.AUTO;
  const ack = auto && task.autoReason === AUTO_REASONS.EMERGENCY_ACK;
  const room = task.roomName ? ` — ${task.roomName}` : "";
  const from = String(task.senderName || "").trim();

  let title;
  if (ack) {
    // Good news, and good news must never be styled or worded like an alarm.
    title = "Your emergency was seen";
  } else if (auto) {
    title = `Board update${room}`;
  } else {
    title = from ? `Directive from ${from}` : `New directive${room}`;
  }

  const text = String(task.text || "").trim();
  const fallback = ack
    ? "A team member has seen your emergency."
    : auto
      ? "The board changed something on your assignment."
      : "The board sent you a directive.";

  return {
    title,
    body: (text || fallback).slice(0, MAX_TASK_TEXT),
    // Collapsing on the task id means a retried delivery cannot stack the same
    // directive twice on a lock screen.
    collapseKey: task.id || undefined,
  };
}

// ── ONE FCM MESSAGE PER DEVICE ───────────────
//
// android priority "normal" + apns-priority 5 + interruption-level "active":
// the phone shows it when the phone is awake, and stays quiet when the person
// has silenced it. `data` carries the ids so tapping can route to the
// directive; `requiresConfirm` rides along so the tap target can be the
// "Got it" control rather than a generic open.
export function buildTaskPushMessage(task, token) {
  const note = buildTaskNotification(task);
  if (!note || !token) return null;
  return {
    token,
    notification: { title: note.title, body: note.body },
    data: {
      taskId: String(task.id || ""),
      kind: String(task.kind || ""),
      autoReason: String(task.autoReason || ""),
      facilityId: String(task.facilityId || ""),
      // Strings only — FCM data payloads carry no booleans.
      requiresConfirm: task.requiresConfirm === false ? "false" : "true",
    },
    android: {
      priority: "normal",
      collapseKey: note.collapseKey,
      notification: {
        channelId: TASK_CHANNEL_ID,
        sound: "default",
        // No defaultVibrateTimings: a buzz in a pocket during a case is the
        // emergency channel's signal, and it must keep meaning only that.
      },
    },
    apns: {
      headers: {
        "apns-priority": "5",
        "apns-collapse-id": note.collapseKey || "",
      },
      payload: {
        aps: {
          sound: "default",
          "interruption-level": "active",
        },
      },
    },
  };
}

// Every message to send for one directive, given each recipient's tokens.
// tokensByUid: { uid: string[] }. A user with several devices gets all of them.
export function buildTaskPushMessages(task, tokensByUid) {
  const messages = [];
  taskPushRecipients(task).forEach((uid) => {
    const tokens = (tokensByUid || {})[uid] || [];
    [...new Set(tokens.filter(Boolean))].forEach((token) => {
      const msg = buildTaskPushMessage(task, token);
      if (msg) messages.push(msg);
    });
  });
  return messages;
}

// ─────────────────────────────────────────────
//  EMERGENCY ACK → a directive back to whoever raised it
//
//  THE GAP: `acknowledgedBy` is read by the board (domain/controlPanel.js,
//  domain/boardWorklist.js, RoomCard.js) and by every recipient's own views.
//  NOTHING tells the person who pressed the button. A CRNA raises an emergency
//  and then stands in the room with no idea whether anyone saw it.
// ─────────────────────────────────────────────

function ackList(alert) {
  return Array.isArray(alert?.acknowledgedBy) ? alert.acknowledgedBy.filter(Boolean) : [];
}

// Who to name in the receipt — or null for "say nothing".
//
// ⚠️ FIRST ACKNOWLEDGER ONLY. The meaningful fact is "someone is coming". The
// second and third acks are noise arriving DURING an emergency, which is the
// worst possible moment for noise: the person's phone buzzing three times while
// they are bagging a patient teaches them to put it down. So if
// `acknowledgedBy` was already non-empty before this write, this returns null
// no matter what changed.
//
// Emergencies only. An urgency ack is a routine ping answered routinely.
export function firstAcknowledgerUid(before, after) {
  if (!after || after.type !== "emergency") return null;
  if (!after.senderId) return null; // nobody to send the receipt to

  // Already acknowledged before this write → this is ack #2 or later.
  if (ackList(before).length > 0) return null;

  // The sender is filtered rather than checked positionally: two acks can land
  // in one write, and if the raiser happens to be first in that array a
  // positional check would drop a REAL acknowledgement on the floor. Filtering
  // keeps both rules — never tell someone they acknowledged themselves, and
  // never lose the first person who actually came.
  const candidates = ackList(after).filter((uid) => uid !== after.senderId);
  return candidates[0] || null;
}

// The task document for that receipt. Returns null when it should not exist, so
// a caller cannot write a directive addressed to nobody.
//
// ⚠️ `requiresConfirm: false` IS LOAD-BEARING. This is a receipt about a
// receipt. Asking someone to acknowledge an acknowledgement is a loop with no
// end, and it would also park a permanent "pending" count on the board for a
// row that was never an instruction.
export function buildEmergencyAckTask({
  alert,
  acknowledgerUid,
  acknowledgerName = "",
  dateKey,
} = {}) {
  if (!alert || !acknowledgerUid || !dateKey) return null;
  const raiser = alert.senderId;
  if (!raiser || raiser === acknowledgerUid) return null;
  if (!alert.facilityId) return null; // unscoped rows are invisible to every board

  const who = String(acknowledgerName || "").trim() || "A team member";
  const room = alert.roomName ? ` — ${alert.roomName}` : "";

  return {
    facilityId: alert.facilityId,
    dateKey,
    // "system", not the acknowledger: the acknowledger did not send a message,
    // the board is reporting a fact. senderName is what the phone card shows.
    senderId: "system",
    senderName: "ATC",
    recipientIds: [raiser],
    text: `${who} acknowledged your emergency${room}.`.slice(0, MAX_TASK_TEXT),
    kind: TASK_KINDS.AUTO,
    autoReason: AUTO_REASONS.EMERGENCY_ACK,
    roomName: alert.roomName || null,
    requiresConfirm: false,
  };
}
